import React, { ChangeEvent, useCallback, useEffect, useState, VFC } from 'react';
import { useNavigate } from 'react-router-dom';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { Box, Divider, Pagination } from '@dxb-web-ui-framework/design-system';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import {
  useNotificationsAuthorizationsList,
  useAuthorizationErrorState,
  useAuthorizationLoadingState,
  useAuthorizationSelectAction,
  useAuthorizationSelectedAuthorization,
  useAuthorizationTransactionDetails,
  useBulkSelectedAuthorizations,
  useCheckAuthorization,
  useIsAuthFlowDrawerOpen,
  useIsAuthorizationTransactionDetailsDrawerOpen,
  useOnToggleAuthFlowDrawer,
  useToggleAuthorizationTransactionDetailsDrawer,
  useNotificationsLoadingState,
  useNotificationsErrorState,
  useAuthAction,
  useOnSetAuthAction,
  useOnShouldShowRequestNewRateBtn,
} from '../contexts';
import { useSelectedCustomerId } from '../../../userProfile/src/contexts/UserInfo/hooks';
import { Mandate } from '../domain/Authorization/entities';
import { getRoutePath } from '../../../content-composer/src/components/Routes/routePaths';
import { renderTableGridActions, TableGrid } from '../../../content-composer/src/components/Generic/TableGrid';
import { ErrorAlert } from '../../../content-composer/src/components/Generic/ErrorAlert/ErrorAlert';
import { TransactionDetailsDrawer } from '../../../transaction/src/components/TransactionDetails/TransactionDetailsDrawer';
import usePagination from '../../../content-composer/src/hooks/usePagination';
import { useAuthorizationGridConfig } from './useAuthorizationGridConfig';
import { ApproveRejectAuthFlowPresenter, ProcessStatuses } from './ApproveRejectAuthFlowPresenter';
import { ApproveRejectAuthDrawer } from './ApproveRejectAuthDrawer';

const AuthorizationsTable: VFC<{ classes: GenericObject }> = ({ classes }) => {
  const isAuthFlowDrawerOpen = useIsAuthFlowDrawerOpen();
  const onToggleAuthFlowDrawer = useOnToggleAuthFlowDrawer();
  const authAction = useAuthAction();
  const setAuthAction = useOnSetAuthAction();
  const [isBulk, setIsBulk] = useState<boolean>(false);
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const navigate = useNavigate();
  const selectedCustomerId = useSelectedCustomerId();
  const rowData = useNotificationsAuthorizationsList();
  const isLoadingAuthorizationList = useNotificationsLoadingState('isLoadingPendingAuthorizations');
  const authorizationListError = useNotificationsErrorState('pendingAuthorizationsListError');
  const onSelectAuthorization = useAuthorizationSelectAction();
  const isOpenDrawer = useIsAuthorizationTransactionDetailsDrawerOpen();
  const toggleDrawer = useToggleAuthorizationTransactionDetailsDrawer();
  const checkAuthorization = useCheckAuthorization();
  const selectedAuthorization = useAuthorizationSelectedAuthorization();
  const selectedAuthorizationTransactionDetails = useAuthorizationTransactionDetails();
  const isLoadingTransactionDetails = useAuthorizationLoadingState('isLoadingTransactionDetails');
  const transactionDetailsError = useAuthorizationErrorState('transactionDetailsError');
  const isLoadingCheckAuthorization = useAuthorizationLoadingState('isLoadingCheckAuthorization');
  const bulkSelectedAuthorizations = useBulkSelectedAuthorizations();
  const shouldShowRequestNewRateBtn = useOnShouldShowRequestNewRateBtn();

  // pagination
  const { setCurrentPage, setData, currentData, currentPage, count, jump } = usePagination(rowData || [], 10);

  useEffect(() => {
    setData(rowData || []);
    setCurrentPage(1);
  }, [setCurrentPage, rowData]);

  const paginatedAuthorizationListData = currentData();

  const onChangePage = useCallback(
    (event: ChangeEvent<unknown>, page: number) => {
      setCurrentPage(page);
      jump(page);
    },
    [jump, setCurrentPage],
  );

  const onViewDetails = useCallback(
    (transactionNo: string) => {
      toggleDrawer();
      onSelectAuthorization(transactionNo);
      setAuthAction(null);
    },
    [onSelectAuthorization, toggleDrawer, setAuthAction],
  );

  const onSelectListItem = useCallback(
    (rowData: Mandate) => {
      if (rowData.processStatus === ProcessStatuses.PENDING) {
        return;
      }
      onSelectAuthorization(rowData.transactionId);
      navigate(
        getRoutePath({
          routeKey: 'account-transaction-authorization-details',
          customerId: selectedCustomerId,
          transactionNo: rowData.globalOrderId,
        }),
      );
    },
    [navigate, onSelectAuthorization, selectedCustomerId],
  );

  const actionCallback = useCallback(
    (event: any, name: string, transactionNo: string, isActionFromDetailsDrawer: boolean) => {
      setIsBulk(false);
      switch (name) {
        case 'approve':
          !isActionFromDetailsDrawer && onToggleAuthFlowDrawer();
          onSelectAuthorization(transactionNo);
          setAuthAction(name);
          break;
        case 'reject':
          !isActionFromDetailsDrawer && onToggleAuthFlowDrawer();
          onSelectAuthorization(transactionNo);
          setAuthAction(name);
          break;
        default:
          onViewDetails(transactionNo!);
      }
    },
    [onToggleAuthFlowDrawer, onViewDetails, onSelectAuthorization, setAuthAction],
  );

  const onBulkMandatesAction = useCallback(
    (name: string) => {
      setIsBulk(true);
      onToggleAuthFlowDrawer();
      setAuthAction(name);
    },
    [onToggleAuthFlowDrawer, setAuthAction],
  );

  const gridConfig = useAuthorizationGridConfig(
    actionCallback,
    bulkSelectedAuthorizations,
    onSelectListItem,
    rowData,
    onBulkMandatesAction,
  );

  return (
    <>
      {!isLoadingAuthorizationList && !isEmpty(gridConfig.actions) && !authorizationListError && (
        <>
          <Box className={classes.tableActionsContainer} ml={0}>
            {renderTableGridActions(gridConfig.actions)}
          </Box>
          {isMobile && <Divider />}
        </>
      )}
      {authorizationListError && (
        <Box mx={3} mt={2}>
          <ErrorAlert message={authorizationListError.message} />
        </Box>
      )}
      {!authorizationListError && (
        <TableGrid
          gridOptions={gridConfig.gridOptions}
          columnDefs={gridConfig.columnDefs}
          rowData={paginatedAuthorizationListData}
          hasSkeleton={isLoadingAuthorizationList}
          mobileListConfig={gridConfig.mobileListConfig}
          isRowSelected={gridConfig.isRowSelected}>
          {count > 1 && (
            <Pagination
              count={count}
              color='primary'
              page={currentPage}
              onChange={onChangePage}
              siblingCount={isMobile ? 0 : undefined}
            />
          )}
        </TableGrid>
      )}
      {!isMobile && isOpenDrawer && (
        <TransactionDetailsDrawer
          isOpen={isOpenDrawer}
          onToggleDrawer={onViewDetails}
          isAuthorization={true}
          isLoading={isLoadingTransactionDetails || isLoadingCheckAuthorization}
          error={transactionDetailsError}
          transaction={selectedAuthorization}
          transactionDetails={selectedAuthorizationTransactionDetails}
          lastAuthorizer={checkAuthorization?.lastAuthorizer ?? false}
          needMandate={checkAuthorization?.needMandate ?? true}
          shouldShowMandateRequestRateButton={shouldShowRequestNewRateBtn}
          shouldShowApproximateRate={true}
          shouldShowChildren={Boolean(authAction) && Boolean(authAction === 'reject' || authAction === 'approve')}
          actionCallback={actionCallback}>
          <ApproveRejectAuthFlowPresenter authAction={authAction} onToggleDrawer={onToggleAuthFlowDrawer} />
        </TransactionDetailsDrawer>
      )}
      {!isOpenDrawer && (
        <ApproveRejectAuthDrawer
          isOpen={isAuthFlowDrawerOpen}
          onToggle={onToggleAuthFlowDrawer}
          authAction={authAction}
          isBulk={isBulk}
          showMandateInfoRateButton={checkAuthorization?.lastAuthorizer && selectedAuthorization?.eligibleForDealerRate}
        />
      )}
    </>
  );
};

export { AuthorizationsTable };
